import * as React from 'react';
import { useMemo } from 'react';
import { ThemeProvider, useTheme } from '../../../theme';
import Included from './Included/Included';
import Upgrade from './Upgrade/Upgrade';
import { Baggage, SelectedBaggageItem } from '../../../BaggageService/types';
import { SelectedUpgrade } from '../types';
import { PassengerLabels } from '../../../Checkout/types';
import BaggageTotalPriceBlock from '../../BaggageTotalPriceBlock';
import { useBaggageToggleable } from '../../hooks';
import UpgradeModal from './UpgradeModal/UpgradeModal';
import { getMinPrice, getPriceToPay } from '../../../BaggagePage/utils';
import { joinIncludedBaggageByRfics, joinServicesByRfics } from '../../UpgradeBaggageDialog/utils';
import BaggageContextProvider from '../../context';
import { Passenger, Segment, SelectedBaggageService } from '../../types';
import cn from 'classnames';
import { ServiceMeta } from '../../../Checkout/store/selectedServices/actions';

interface Props {
	passengers: Passenger[];
	passengerLabels: PassengerLabels;
	segments: Segment[];
	includedBaggage: SelectedBaggageItem[];
	upgradeServices: Baggage[];
	selectedServices: SelectedUpgrade[];
	isLoading: boolean;
	disabled?: boolean;
	dynamicBaggagePricing?: boolean;
	className?: string;
	analyticsMeta?: ServiceMeta;
	onDelete?: (servicesToDelete: SelectedBaggageService[]) => void;
}

const IncludedBaggage: React.FC<Props> = props => {
	const { IncludedBaggage: css } = useTheme('IncludedBaggage');
	const { BaggageList: listCss } = useTheme('BaggageList');

	const showUpgradeServices = !!props.upgradeServices?.length;

	const { isOpen, open, close } = useBaggageToggleable(props.upgradeServices);

	const priceToPay = useMemo(() => getPriceToPay(props.selectedServices, props.upgradeServices), [
		props.upgradeServices,
		props.selectedServices
	]);

	const baggageByRfics = useMemo(() => joinServicesByRfics(props.upgradeServices), [props.upgradeServices]);

	const minPrice = useMemo(() => getMinPrice(props.upgradeServices), [props.upgradeServices]);

	const groupedByRficsIncludedBaggage = useMemo(() => {
		return joinIncludedBaggageByRfics(props.includedBaggage);
	}, [props.includedBaggage]);

	const baggageListTheme = useMemo(
		() => ({
			BaggageList: {
				BaggageList: {
					root: cn({
						[listCss.root_withUpgrade]: showUpgradeServices
					})
				}
			}
		}),
		[listCss, showUpgradeServices]
	);

	return (
		<div className={cn(css.root, props.className)}>
			<div className={cn(css.options, { [css.options_withoutUpgrade]: !showUpgradeServices })}>
				<ThemeProvider value={baggageListTheme}>
					{props.includedBaggage.length > 0 && (
						<Included
							className={css.included_block}
							segments={props.segments}
							includedBaggage={groupedByRficsIncludedBaggage}
							sliderToShow={showUpgradeServices ? 1 : 2}
						/>
					)}
					{showUpgradeServices && (
						<BaggageContextProvider
							value={{
								onBaggageIconClick: open,
								onBaggageDelete: props.onDelete
							}}
						>
							<Upgrade
								className={css.upgrade_block}
								upgradeServices={baggageByRfics}
								upgradeMinPrice={minPrice}
								selectedServices={props.selectedServices}
								segments={props.segments}
							/>
						</BaggageContextProvider>
					)}
				</ThemeProvider>
			</div>

			{showUpgradeServices && (
				<div className={css.footer}>
					<BaggageTotalPriceBlock total={priceToPay} min={minPrice} onClickButton={open} />
				</div>
			)}

			{showUpgradeServices && (
				<UpgradeModal
					className={props.className}
					open={isOpen}
					onClose={close}
					passengers={props.passengers}
					passengerLabels={props.passengerLabels}
					segments={props.segments}
					upgradeBaggage={props.upgradeServices}
					disabled={props.isLoading || props.disabled}
					selectedServices={props.selectedServices}
					dynamicBaggagePricing={props.dynamicBaggagePricing}
					isLoading={props.isLoading}
					analyticsMeta={props.analyticsMeta}
				/>
			)}
		</div>
	);
};

export default IncludedBaggage;
